import { useCallback, useMemo, useRef } from 'react'

import {
  clamp,
  getMinTop,
  KEYBOARD_STEP,
  VIEWPORT_MARGIN,
} from './viewportBounds'

// The bottom-right corner uses the browser's own resize handle.
export const CORNERS = ['top-left', 'top-right', 'bottom-left']

// Arrow keys move the corner, so the arrow pointing away from the window grows it.
const KEY_DELTAS = {
  ArrowLeft: [-KEYBOARD_STEP, 0],
  ArrowRight: [KEYBOARD_STEP, 0],
  ArrowUp: [0, -KEYBOARD_STEP],
  ArrowDown: [0, KEYBOARD_STEP],
}

/**
 * Resizes the window from its top-left, top-right and bottom-left corners, keeping
 * the opposite corner in place. The native CSS resize handle only exists at the
 * bottom-right, so this writes width/height to the element the same way it does.
 * @param {Object} windowRef - Ref to the window element
 * @param {{ x: number, y: number }} position - Current window position
 * @param {Function} setPosition - Position setter from useDraggable
 * @returns {Object} - Pointer and keyboard handler props for each corner in CORNERS
 */
function useCornerResize(windowRef, position, setPosition) {
  const startRef = useRef(null)

  const snapshot = useCallback(() => ({
    x: position.x,
    y: position.y,
    width: windowRef.current.offsetWidth,
    height: windowRef.current.offsetHeight,
  }), [windowRef, position])

  const resize = useCallback((corner, start, dx, dy) => {
    const node = windowRef.current
    const style = window.getComputedStyle(node)
    const minWidth = parseFloat(style.minWidth) || 0
    const minHeight = parseFloat(style.minHeight) || 0
    const movesLeftEdge = corner.endsWith('left')
    const movesTopEdge = corner.startsWith('top')
    const right = start.x + start.width
    const bottom = start.y + start.height

    const width = movesLeftEdge
      ? clamp(start.width - dx, minWidth, right - VIEWPORT_MARGIN)
      : clamp(start.width + dx, minWidth, window.innerWidth - VIEWPORT_MARGIN - start.x)
    const height = movesTopEdge
      ? clamp(start.height - dy, minHeight, bottom - getMinTop())
      : clamp(start.height + dy, minHeight, window.innerHeight - VIEWPORT_MARGIN - start.y)

    node.style.width = `${width}px`
    node.style.height = `${height}px`
    setPosition({
      x: movesLeftEdge ? right - width : start.x,
      y: movesTopEdge ? bottom - height : start.y,
    })
  }, [windowRef, setPosition])

  const handlePointerMove = useCallback((e) => {
    const start = startRef.current
    if (!start) {
      return
    }
    resize(start.corner, start, e.clientX - start.pointerX, e.clientY - start.pointerY)
  }, [resize])

  const handlePointerUp = useCallback((e) => {
    if (!startRef.current) {
      return
    }
    startRef.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
  }, [])

  return useMemo(() => Object.fromEntries(CORNERS.map(corner => [corner, {
    onPointerDown: (e) => {
      if (e.button !== 0) {
        return
      }
      e.preventDefault()
      startRef.current = { ...snapshot(), corner, pointerX: e.clientX, pointerY: e.clientY }
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerUp,
    onKeyDown: (e) => {
      const delta = KEY_DELTAS[e.key]
      if (!delta) {
        return
      }
      e.preventDefault()
      resize(corner, snapshot(), delta[0], delta[1])
    },
  }])), [snapshot, resize, handlePointerMove, handlePointerUp])
}

export default useCornerResize
